/**
 * Drives the "Continue" action on the post-login passkey-setup interstitial
 * (app/Service/Web/Views/auth/passkey-setup.twig).
 *
 * The interstitial enrolls a passkey without a current password: the user
 * just authenticated with their password at login, which opened a short-lived
 * server-side grace window (see WebAuthnService::grantEnrollGrace). So the
 * ceremony POSTs to /v1/webauthn-registration{/options} with no currentPassword
 * and relies on that marker. WebAuthn helpers come from window.WebAuthnHelpers;
 * HTTP goes through the shared ApiClient (JWT-aware). The platform-authenticator
 * support check and reveal/bounce live inline in the Twig template.
 */
(function () {
    'use strict';

    function ready(fn) {
        if (document.readyState !== 'loading') {
            fn();
        } else {
            document.addEventListener('DOMContentLoaded', fn);
        }
    }

    function helpers() {
        return window.WebAuthnHelpers;
    }

    function translate(key, fallback) {
        return helpers() ? helpers().translate('auth', key, fallback) : fallback;
    }

    function suggestedName(defaultName) {
        const h = helpers();
        if (!h) return defaultName;
        const browser = h.detectBrowserLabel(navigator.userAgent);
        const platformHint = navigator.userAgentData?.platform || navigator.platform || '';
        const platform = h.detectPlatformLabel(navigator.userAgent, platformHint, navigator.maxTouchPoints);
        return h.getSuggestedPasskeyName(defaultName, browser, platform);
    }

    ready(function () {
        const root = document.getElementById('passkey-setup-root');
        const btn = document.getElementById('passkey-setup-continue');
        if (!root || !btn) return;

        const dest = root.getAttribute('data-dest') || '/app/account';
        const defaultName = root.getAttribute('data-default-name') || 'Passkey';
        const statusEl = document.getElementById('passkey-setup-status');
        const spinner = document.getElementById('passkey-setup-spinner');

        function showError(message) {
            if (!statusEl) return;
            statusEl.textContent = message;
            statusEl.classList.remove('hidden');
        }

        function clearError() {
            if (!statusEl) return;
            statusEl.textContent = '';
            statusEl.classList.add('hidden');
        }

        function setBusy(busy) {
            btn.disabled = busy;
            if (spinner) spinner.classList.toggle('hidden', !busy);
        }

        async function errorFor(res) {
            const fallback = translate(
                'passkey_setup_error_generic',
                'Could not add a passkey. This device may already be set up.'
            );
            try {
                const classification = await helpers().classifyPasskeyError(res);
                if (classification.kind === 'rate_limited') {
                    return translate(
                        'passkey_error_rate_limited',
                        'Too many attempts. Wait a moment and try again.'
                    );
                }
                return fallback;
            } catch (_e) {
                return fallback;
            }
        }

        btn.addEventListener('click', async function () {
            if (!helpers() || !window.ApiClient || !helpers().isWebAuthnSupported()) return;

            clearError();
            setBusy(true);
            try {
                const optionsRes = await window.ApiClient.request('/webauthn-registration/options', {
                    method: 'POST',
                    body: new FormData(),
                });
                if (!optionsRes.ok) {
                    showError(await errorFor(optionsRes));
                    setBusy(false);
                    return;
                }
                const options = await optionsRes.json();
                const decoded = helpers().decodeCreateOptions(options.publicKey);

                let credential;
                try {
                    credential = await navigator.credentials.create({ publicKey: decoded });
                } catch (e) {
                    if (e && (e.name === 'NotAllowedError' || e.name === 'AbortError')) {
                        // User dismissed the platform prompt. Let them retry
                        // or skip; the grace window is still open.
                        setBusy(false);
                        return;
                    }
                    showError(
                        e && e.name === 'InvalidStateError'
                            ? translate('passkey_setup_error_already_exists', 'This device already has a passkey for your account.')
                            : translate('passkey_setup_error_generic', 'Could not add a passkey. This device may already be set up.')
                    );
                    if (window.console) console.warn('passkey-setup:', e.name, e.message);
                    setBusy(false);
                    return;
                }

                if (!credential) {
                    setBusy(false);
                    return;
                }

                const formData = new FormData();
                formData.append('challengeId', options.challengeId);
                formData.append('clientDataJSON', helpers().base64UrlEncode(credential.response.clientDataJSON));
                formData.append('attestationObject', helpers().base64UrlEncode(credential.response.attestationObject));
                formData.append('name', suggestedName(defaultName));

                const verifyRes = await window.ApiClient.request('/webauthn-registration', {
                    method: 'POST',
                    body: formData,
                });
                if (!verifyRes.ok) {
                    showError(await errorFor(verifyRes));
                    setBusy(false);
                    return;
                }

                window.location.href = dest;
            } catch (e) {
                showError(translate(
                    'passkey_setup_error_generic',
                    'Could not add a passkey. This device may already be set up.'
                ));
                if (window.console) console.warn('passkey-setup:', e);
                setBusy(false);
            }
        });

        // Handler attached: enable the button (server renders it disabled so an
        // early click before hydration is not a silent no-op).
        btn.disabled = false;
    });
})();
